import { cn } from "@/lib/utils";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import { Button, type ButtonProps, type buttonVariants } from "./button";

type RadioGroupContextValue = {
  value?: string;
  onValueChange?: (value: string) => void;
  name?: string;
  variant: NonNullable<VariantProps<typeof buttonVariants>["variant"]>;
};

const RadioGroupContext = React.createContext<RadioGroupContextValue | undefined>(undefined);

function useRadioGroupContext() {
  const context = React.useContext(RadioGroupContext);
  if (!context) {
    throw new Error("RadioGroupItem must be used within a RadioGroup");
  }
  return context;
}

interface RadioGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  className?: string;
  gap?: number;
  name?: string;
  variant?: NonNullable<VariantProps<typeof buttonVariants>["variant"]>;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

export function RadioGroup({
  value,
  onValueChange,
  defaultValue,
  className,
  gap = 2,
  name,
  variant = "secondary",
  children,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  ...props
}: RadioGroupProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const currentValue = value ?? internalValue;

  const handleValueChange = React.useCallback(
    (newValue: string) => {
      setInternalValue(newValue);
      onValueChange?.(newValue);
    },
    [onValueChange],
  );

  // Generate a unique name if not provided
  const generatedName = React.useId();
  const radioGroupName = name || generatedName;

  return (
    <RadioGroupContext.Provider
      value={{ value: currentValue, onValueChange: handleValueChange, name: radioGroupName, variant }}
    >
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        className={cn("flex flex-wrap items-center", `gap-${gap}`, className)}
        {...props}
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

interface RadioGroupItemProps extends Omit<ButtonProps, "value" | "onChange" | "variant" | "className"> {
  value: string;
  onChange?: (value: string) => void;
  className?: string;
}

export function RadioGroupItem({ value, onChange, className, children, ...props }: RadioGroupItemProps) {
  const { value: groupValue, onValueChange, name, variant } = useRadioGroupContext();
  const isSelected = value === groupValue;

  const handleClick = React.useCallback(() => {
    onChange?.(value);
    onValueChange?.(value);
  }, [onChange, onValueChange, value]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const id = React.useId();

  return (
    <label className="contents">
      <input
        type="radio"
        id={id}
        className="sr-only"
        name={name}
        value={value}
        checked={isSelected}
        onChange={handleClick}
      />
      <Button
        type="button"
        variant={isSelected ? variant : "outlined"}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          isSelected
            ? {
                black: "hover:bg-textSecondary hover:border-textSecondary",
                ghost: "hover:bg-transparent hover:border-transparent",
                outlined: "hover:bg-white hover:border-borderLight",
                secondary: "hover:bg-borderLight hover:border-borderLight",
                error: "hover:bg-colorRed hover:border-colorRed",
              }[variant]
            : "hover:bg-white hover:border-borderLight",
          "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-textPrimary focus-visible:outline-none",
          isSelected ? "cursor-default" : "cursor-pointer",
          className,
        )}
        {...props}
      >
        {children}
      </Button>
    </label>
  );
}
